import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ChevronRight,
  ArrowLeft,
  AlertCircle,
  Lock,
  Play,
  Loader2,
} from 'lucide-react';
import { api, ApiError } from '@/api/client';
import type {
  CreateReq,
  CreateResp,
  GetResourcesResp,
  PreviewReq,
  PreviewResp,
  Resource,
  ResourceType,
} from '@/api/types';
import {
  parseMemoryBytes,
  formatBytes,
  parseStringListItems,
  parseCollectionLive,
  toBase64,
  fromBase64,
  validateResourceName,
} from '@/lib/resource-helpers';
import { RESOURCE_TYPES } from '@/components/resources/TypePicker';
import Section from '@/components/resources/form/Section';
import Field from '@/components/resources/form/Field';
import SoftLimitField from '@/components/resources/form/SoftLimitField';
import ToggleChip from '@/components/resources/form/ToggleChip';
import CheckboxRow from '@/components/resources/form/CheckboxRow';
import PreviewResult from '@/components/resources/form/PreviewResult';
import CodeField from '@/components/resources/form/CodeField';
import { inputClass, textareaClass } from '@/components/resources/form/styles';
import { extractApiMessage } from '@/components/resources/form/errors';

// The legacy /modifyresource handler refuses NFT-license-token names
// (any name containing '@' that parses as a hedera NftID — see
// pkg/ui/modifyresourcehandler.go:298). The v1 modify endpoint inherits
// the same restriction indirectly through internal/db/resource/
// resourceDB.go:215 nftToken(). We don't have the SDK in the browser,
// so the heuristic is just "name contains an '@'" — the backend gives
// the authoritative error if our guess is wrong.
function looksLikeNftName(name: string): boolean {
  return name.includes('@');
}

// Memory values come back through MemoryConverter.ToReadable, which produces
// strings like "64 GiB" or "1.5 GB". Those parse just fine via
// parseMemoryBytes when the user re-submits, but the trailing zero noise
// from Double's sprintf("%f") makes the input look bad on first load.
// Normalize the Double prefill so "3.140000" becomes "3.14".
function prettyDouble(raw: string): string {
  if (!raw) return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toString();
}

export default function ModifyResourcePage() {
  const { name: nameParam = '' } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // GET wraps the single resource in the same `resources: [...]` shape
  // used for the list endpoint (internal/handler/v1/resource/get.go:52).
  const {
    data,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['resource', nameParam],
    queryFn: () =>
      api<GetResourcesResp>(`/v1/resources/${encodeURIComponent(nameParam)}`),
    enabled: nameParam !== '',
  });

  const resource = data?.resources?.[0];
  const type = resource?.type as ResourceType | undefined;
  // The type is shown in the breadcrumb as its icon (with the friendly
  // label as a tooltip + screen-reader text), matching how the type picker
  // and create flow depict types. Both are empty until the resource loads,
  // so the crumb shows just the name first.
  const typeDef = type ? RESOURCE_TYPES.find((t) => t.key === type) : undefined;
  const typeLabel = typeDef?.label ?? type ?? '';
  const TypeIcon = typeDef?.icon;

  return (
    <div className="space-y-6">
      {/* Breadcrumb is the only title: resource name in bold with its type
          in parens, so the identity isn't repeated in a separate heading
          (RM-120). */}
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-sm text-muted-foreground"
      >
        <Link
          to="/resources"
          className="hover:text-foreground transition-colors inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
        {TypeIcon && (
          <span
            role="img"
            aria-label={typeLabel}
            title={typeLabel}
            className="inline-flex shrink-0 text-muted-foreground"
          >
            <TypeIcon className="h-4 w-4" aria-hidden />
          </span>
        )}
        <span className="font-mono font-semibold text-foreground truncate max-w-[40ch]">
          {nameParam}
        </span>
        <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
        <span className="text-foreground">Modify</span>
      </nav>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading resource…
        </div>
      )}

      {fetchError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Failed to load resource</div>
            <div className="text-xs mt-0.5 opacity-90">
              {extractApiMessage(fetchError)}
            </div>
          </div>
        </div>
      )}

      {/* Render the editor only once we have the resource and a known type.
          A null type would mean the API returned the empty list (which
          internal/handler/v1/resource/get.go currently treats as 500, so
          we get fetchError in practice — but the guard keeps the form
          honest if that ever changes). */}
      {resource && type && (
        <ModifyForm
          resource={resource}
          type={type}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['resources'] });
            queryClient.invalidateQueries({ queryKey: ['resource', nameParam] });
            navigate('/resources');
          }}
        />
      )}
    </div>
  );
}

interface ModifyFormProps {
  resource: Resource;
  type: ResourceType;
  onSaved: () => void;
}

function ModifyForm({ resource, type, onSaved }: ModifyFormProps) {
  // Each field's initial value is derived once from the loaded resource;
  // useState's initializer pattern avoids reconciliation on subsequent
  // renders (we don't want a query refetch to clobber the user's edits).
  const [description, setDescription] = useState(() => resource.description ?? '');
  const [value, setValue] = useState(() => initialValue(resource, type));
  const [softLimit, setSoftLimit] = useState(() =>
    initialSoftLimit(resource, type),
  );

  // Measurement-only state.
  const [measurementContainer, setMeasurementContainer] = useState(
    () => resource.measurementContainer ?? '',
  );
  const [measurementInterval, setMeasurementInterval] = useState(() =>
    resource.measurementIntervalInSeconds != null
      ? String(resource.measurementIntervalInSeconds)
      : '',
  );
  const [measurementMaxValue, setMeasurementMaxValue] = useState(() => {
    const m = resource.measurementMaxValue;
    // The converter encodes "unbounded" as -1; show that as empty so the
    // user doesn't see a magic sentinel in the input.
    if (m == null || m < 0) return '';
    return String(m);
  });
  const [measurementTimeout, setMeasurementTimeout] = useState(() =>
    resource.measurementTimeout != null
      ? String(resource.measurementTimeout)
      : '',
  );

  // Collection-only state. The new round-trip flags on the response let
  // us preserve YAML/JSON and uniform choices.
  const [collectionFormat, setCollectionFormat] = useState<'yaml' | 'json'>(
    () => (resource.jsonCollection ? 'json' : 'yaml'),
  );
  const [shareable, setShareable] = useState(
    () => resource.shareableCollection === true,
  );
  const [uniform, setUniform] = useState(
    () => resource.uniformCollection === true,
  );

  const [preview, setPreview] = useState<PreviewResp | null>(null);
  // Measurement script editor — highlighting on by default; off renders a
  // plain textarea and never loads the CodeMirror chunk.
  const [scriptHighlight, setScriptHighlight] = useState(true);

  const isNft = looksLikeNftName(resource.name);

  const updateMutation = useMutation({
    mutationFn: (req: CreateReq) =>
      api<CreateResp>(`/v1/resources/${encodeURIComponent(resource.name)}`, {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: onSaved,
  });

  const previewMutation = useMutation({
    mutationFn: (req: PreviewReq) =>
      api<PreviewResp>('/v1/measurement-preview', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: (resp) => setPreview(resp),
    onError: (err) => {
      if (err instanceof ApiError && err.body && typeof err.body === 'object') {
        setPreview(err.body as PreviewResp);
      } else {
        setPreview({
          error: true,
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      }
    },
  });

  // Name doesn't change — but we still validate the *original* once, so if
  // a name slipped through (older API) we surface it consistently.
  const nameError = useMemo(
    () => validateResourceName(resource.name),
    [resource.name],
  );

  const valueValidation = useMemo<{
    error?: string;
    hint?: string;
    okHint?: string;
  }>(() => {
    if (value === '') return {};
    switch (type) {
      case 'LicenseCounter': {
        const n = Number(value.trim());
        if (!Number.isInteger(n)) return { error: 'Must be a whole number' };
        if (n < 0) return { error: 'Must be ≥ 0' };
        return { okHint: `Will be stored as ${n}` };
      }
      case 'Double': {
        const n = Number(value.trim());
        if (!Number.isFinite(n)) return { error: 'Must be a number' };
        if (n < 0) return { error: 'Must be ≥ 0' };
        return { okHint: `Will be stored as ${n}` };
      }
      case 'Memory': {
        const bytes = parseMemoryBytes(value);
        if (bytes === null)
          return { error: 'Expected a value like 1GB, 512MiB, or 1024' };
        return { okHint: `≈ ${formatBytes(bytes)} bytes` };
      }
      case 'StringList': {
        const items = parseStringListItems(value);
        if (items.length === 0) return { error: 'At least one item required' };
        return {
          okHint: `${items.length} item${items.length === 1 ? '' : 's'}`,
        };
      }
      case 'Collection': {
        const parsed = parseCollectionLive(value, collectionFormat);
        if (parsed.error) return { error: parsed.error };
        const uniformHint = uniform
          ? parsed.uniform === false
            ? '⚠ items do not appear uniform (will be rejected on save)'
            : '✓ items appear uniform'
          : '';
        return {
          okHint:
            `${parsed.items.length} item${parsed.items.length === 1 ? '' : 's'}` +
            (uniformHint ? ` · ${uniformHint}` : ''),
        };
      }
      default:
        return {};
    }
  }, [type, value, collectionFormat, uniform]);

  const softLimitError = useMemo<string | null>(() => {
    if (softLimit === '') return null;
    if (type === 'LicenseCounter') {
      const v = Number(value.trim());
      const sl = Number(softLimit.trim());
      if (!Number.isInteger(sl) || sl <= 0) return 'Must be a positive integer';
      if (Number.isInteger(v) && sl >= v)
        return `Must be smaller than the resource value (${v})`;
      return null;
    }
    if (type === 'Double') {
      const v = Number(value.trim());
      const sl = Number(softLimit.trim());
      if (!Number.isFinite(sl) || sl <= 0) return 'Must be a positive number';
      if (Number.isFinite(v) && sl >= v)
        return `Must be smaller than the resource value (${v})`;
      return null;
    }
    if (type === 'Memory') {
      const sl = parseMemoryBytes(softLimit);
      const v = parseMemoryBytes(value);
      if (sl === null || sl <= 0)
        return 'Expected a positive memory value (e.g. 256MB)';
      if (v !== null && sl >= v)
        return `Must be smaller than the resource value`;
      return null;
    }
    if (type === 'Measurement') {
      const sl = Number(softLimit.trim());
      const max = Number(measurementMaxValue.trim());
      if (!Number.isInteger(sl) || sl <= 0) return 'Must be a positive integer';
      if (Number.isInteger(max) && max > 0 && sl >= max)
        return `Must be smaller than the maximum value (${max})`;
      return null;
    }
    return null;
  }, [type, softLimit, value, measurementMaxValue]);

  const measurementErrors = useMemo<Partial<Record<string, string>>>(() => {
    if (type !== 'Measurement') return {};
    const errs: Partial<Record<string, string>> = {};
    if (!measurementContainer.trim()) {
      errs.container = 'Container image is required';
    }
    if (measurementInterval !== '') {
      const n = Number(measurementInterval.trim());
      if (!Number.isInteger(n) || n < 0) {
        errs.interval = 'Must be a non-negative integer (0 to disable schedule)';
      }
    }
    if (measurementTimeout !== '') {
      const n = Number(measurementTimeout.trim());
      if (!Number.isInteger(n) || n < 0)
        errs.timeout = 'Must be a non-negative integer';
    }
    if (measurementMaxValue !== '') {
      const n = Number(measurementMaxValue.trim());
      if (!Number.isInteger(n) || n < 0) errs.max = 'Must be a non-negative integer';
    }
    return errs;
  }, [
    type,
    measurementContainer,
    measurementInterval,
    measurementTimeout,
    measurementMaxValue,
  ]);

  const canSubmit = useMemo(() => {
    if (isNft) return false;
    if (updateMutation.isPending) return false;
    if (nameError) return false;
    if (softLimitError) return false;
    if (type !== 'String' && value.trim() === '') return false;
    if (valueValidation.error) return false;
    if (type === 'Measurement') {
      if (Object.keys(measurementErrors).length > 0) return false;
    }
    return true;
  }, [
    isNft,
    nameError,
    softLimitError,
    type,
    value,
    valueValidation.error,
    measurementErrors,
    updateMutation.isPending,
  ]);

  // Reasons Save is disabled, from the same checks as canSubmit, surfaced
  // in a summary by the button so a disabled Save explains itself (RM-120).
  // NFT resources are covered by their own block banner, so we skip the
  // summary for them.
  const blockers = useMemo<string[]>(() => {
    if (isNft) return [];
    const out: string[] = [];
    if (nameError) out.push(nameError);
    if (type !== 'String' && value.trim() === '') {
      const noun =
        type === 'StringList'
          ? 'Items'
          : type === 'Collection'
            ? 'Payload'
            : type === 'Measurement'
              ? 'Script'
              : 'Value';
      out.push(`${noun} is required`);
    }
    if (valueValidation.error) out.push(valueValidation.error);
    if (softLimitError) out.push(`Soft limit: ${softLimitError}`);
    if (type === 'Measurement') {
      for (const m of Object.values(measurementErrors)) if (m) out.push(m);
    }
    return out;
  }, [isNft, nameError, type, value, valueValidation.error, softLimitError, measurementErrors]);

  const showBlockers =
    !isNft && !canSubmit && !updateMutation.isPending && blockers.length > 0;

  const buildPayload = (): CreateReq => {
    // Name must match the URL — internal/handler/v1/resource/post.go
    // rejects mismatches with HTTP 400.
    const base: CreateReq = {
      name: resource.name,
      type,
      description: description.trim() || undefined,
    };
    switch (type) {
      case 'LicenseCounter':
      case 'Double':
      case 'StringList':
      case 'Memory':
        base.value = value.trim();
        // Sending an empty softLimit explicitly clears it
        // (pkg/ui/modifyresourcehandler.go:333 same semantics).
        base.softLimit = softLimit.trim();
        return base;
      case 'String':
        base.value = toBase64(value);
        return base;
      case 'Collection':
        base.value = toBase64(value);
        base.jsonCollection = collectionFormat === 'json';
        base.shareableCollection = shareable;
        base.uniformCollection = uniform;
        return base;
      case 'Measurement': {
        base.value = toBase64(value);
        base.measurementContainer = measurementContainer.trim();
        if (measurementInterval.trim() !== '') {
          base.measurementIntervalInSeconds = Number(measurementInterval.trim());
        }
        if (measurementTimeout.trim() !== '') {
          base.measurementTimeout = Number(measurementTimeout.trim());
        }
        if (measurementMaxValue.trim() !== '') {
          base.measurementMaxValue = Number(measurementMaxValue.trim());
        }
        base.softLimit = softLimit.trim();
        return base;
      }
    }
    return base;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    updateMutation.mutate(buildPayload());
  };

  const handlePreview = () => {
    if (type !== 'Measurement') return;
    const req: PreviewReq = {
      measurementName: resource.name,
      script: toBase64(value),
      image: measurementContainer.trim(),
      timeout: measurementTimeout.trim() || '60',
    };
    previewMutation.mutate(req);
  };

  // Clear any stale preview if the user switches container/script before
  // re-running. Tied to the same inputs the preview button sends.
  useEffect(() => {
    if (preview) setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementContainer, value]);

  const submitError = updateMutation.error
    ? extractApiMessage(updateMutation.error)
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {isNft && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">NFT license tokens cannot be modified</div>
            <div className="text-xs mt-0.5 opacity-90">
              This resource's name looks like an NFT license token (contains
              "@"). The server rejects modifications to NFT-backed resources.
            </div>
          </div>
        </div>
      )}

      <Section>
        <div className="space-y-4">
          <Field
            label="Description"
            htmlFor="description"
            help="Optional. Shown alongside the resource in the list and to API consumers."
          >
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this resource represent?"
              className={inputClass(false)}
              disabled={updateMutation.isPending || isNft}
            />
          </Field>
          {renderTypeFields()}
        </div>
      </Section>

      {submitError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Failed to save changes</div>
            <div className="text-xs mt-0.5 opacity-90">{submitError}</div>
          </div>
        </div>
      )}

      {showBlockers && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border bg-muted/40 p-3 text-sm"
        >
          <div className="flex items-start gap-2">
            <AlertCircle
              className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground"
              aria-hidden
            />
            <div>
              <div className="font-medium">Can’t save yet</div>
              <ul className="mt-1 text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                {blockers.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Link
          to="/resources"
          className="inline-flex items-center px-4 py-2 rounded-md text-sm border hover:bg-muted transition-colors"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {updateMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          Save changes
        </button>
      </div>
    </form>
  );

  function renderTypeFields() {
    switch (type) {
      case 'LicenseCounter':
        return (
          <div className="space-y-4">
            <Field
              label="Total available"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help="Non-negative integer. Allocations count down from this total."
            >
              <input
                id="value"
                type="text"
                inputMode="numeric"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={inputClass(!!valueValidation.error)}
                disabled={updateMutation.isPending || isNft}
              />
            </Field>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="80"
              help="Optional. Empty clears the soft limit."
              disabled={updateMutation.isPending || isNft}
            />
          </div>
        );
      case 'String':
        return (
          <Field
            label="Value"
            htmlFor="value"
            help="The literal string returned to every request."
            required
          >
            <textarea
              id="value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
              className={textareaClass(false)}
              disabled={updateMutation.isPending || isNft}
            />
          </Field>
        );
      case 'StringList':
        return (
          <Field
            label="Items (space-separated)"
            htmlFor="value"
            error={valueValidation.error ?? null}
            hint={valueValidation.okHint ?? null}
            help="One pool, each space-separated token is an allocatable item."
            required
          >
            <textarea
              id="value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={3}
              className={textareaClass(!!valueValidation.error)}
              disabled={updateMutation.isPending || isNft}
            />
          </Field>
        );
      case 'Double':
        return (
          <div className="space-y-4">
            <Field
              label="Total available"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help="Non-negative number. Fractional values allowed."
            >
              <input
                id="value"
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={inputClass(!!valueValidation.error)}
                disabled={updateMutation.isPending || isNft}
              />
            </Field>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="2.5"
              help="Optional. Empty clears the soft limit."
              disabled={updateMutation.isPending || isNft}
            />
          </div>
        );
      case 'Memory':
        return (
          <div className="space-y-4">
            <Field
              label="Total available"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help="Accepts human units (1GB, 512MiB) or raw bytes."
            >
              <input
                id="value"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={inputClass(!!valueValidation.error)}
                disabled={updateMutation.isPending || isNft}
              />
            </Field>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="48GiB"
              help="Optional. Empty clears the soft limit."
              disabled={updateMutation.isPending || isNft}
            />
          </div>
        );
      case 'Collection':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Field
                label="Format"
                htmlFor="format-yaml"
                help="Wire format of the payload below."
              >
                <div className="flex gap-2">
                  <ToggleChip
                    id="format-yaml"
                    label="YAML"
                    selected={collectionFormat === 'yaml'}
                    onClick={() => setCollectionFormat('yaml')}
                    disabled={updateMutation.isPending || isNft}
                  />
                  <ToggleChip
                    id="format-json"
                    label="JSON"
                    selected={collectionFormat === 'json'}
                    onClick={() => setCollectionFormat('json')}
                    disabled={updateMutation.isPending || isNft}
                  />
                </div>
              </Field>
              <Field
                label="Shareable"
                htmlFor="shareable"
                help="When set, allocations don't deplete the supply."
              >
                <CheckboxRow
                  id="shareable"
                  checked={shareable}
                  onChange={setShareable}
                  label="Allocations don't deplete supply"
                  disabled={updateMutation.isPending || isNft}
                />
              </Field>
              <Field
                label="Uniform"
                htmlFor="uniform"
                help="When set, every item must share the same top-level key set."
              >
                <CheckboxRow
                  id="uniform"
                  checked={uniform}
                  onChange={setUniform}
                  label="Items share the same keys"
                  disabled={updateMutation.isPending || isNft}
                />
              </Field>
            </div>
            <Field
              label="Payload"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help={
                collectionFormat === 'yaml'
                  ? 'YAML block sequence. Each "- " starts a new item.'
                  : 'JSON array of items. Each element becomes one allocatable item.'
              }
            >
              <CodeField
                language={collectionFormat === 'json' ? 'json' : 'yaml'}
                value={value}
                onChange={setValue}
                editable={!(updateMutation.isPending || isNft)}
                invalid={!!valueValidation.error}
                rows={10}
                placeholder={
                  collectionFormat === 'yaml'
                    ? '- host: node-01\n  cpu: 64\n- host: node-02\n  cpu: 96'
                    : '[\n  {"host": "node-01", "cpu": 64},\n  {"host": "node-02", "cpu": 96}\n]'
                }
                ariaLabel="Collection payload"
                data-testid="value-editor"
              />
            </Field>
          </div>
        );
      case 'Measurement':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field
                label="Container image"
                htmlFor="container"
                required
                error={measurementErrors.container ?? null}
                help="Image the script runs in."
              >
                <input
                  id="container"
                  type="text"
                  value={measurementContainer}
                  onChange={(e) => setMeasurementContainer(e.target.value)}
                  className={inputClass(!!measurementErrors.container)}
                  disabled={updateMutation.isPending || isNft}
                />
              </Field>
              <Field
                label="Interval (s)"
                htmlFor="interval"
                error={measurementErrors.interval ?? null}
                help="How often the script runs. 0 disables the schedule."
              >
                <input
                  id="interval"
                  type="text"
                  inputMode="numeric"
                  value={measurementInterval}
                  onChange={(e) => setMeasurementInterval(e.target.value)}
                  className={inputClass(!!measurementErrors.interval)}
                  disabled={updateMutation.isPending || isNft}
                />
              </Field>
              <Field
                label="Max value"
                htmlFor="max"
                error={measurementErrors.max ?? null}
                help="Upper bound for allocations. Leave blank for unbounded."
              >
                <input
                  id="max"
                  type="text"
                  inputMode="numeric"
                  value={measurementMaxValue}
                  onChange={(e) => setMeasurementMaxValue(e.target.value)}
                  className={inputClass(!!measurementErrors.max)}
                  placeholder="(unbounded)"
                  disabled={updateMutation.isPending || isNft}
                />
              </Field>
              <Field
                label="Timeout (s)"
                htmlFor="timeout"
                error={measurementErrors.timeout ?? null}
                help="How long a single run is allowed to take."
              >
                <input
                  id="timeout"
                  type="text"
                  inputMode="numeric"
                  value={measurementTimeout}
                  onChange={(e) => setMeasurementTimeout(e.target.value)}
                  className={inputClass(!!measurementErrors.timeout)}
                  disabled={updateMutation.isPending || isNft}
                />
              </Field>
            </div>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="80"
              help="Optional. Empty clears the soft limit. Must be smaller than the maximum value when set."
              disabled={updateMutation.isPending || isNft}
            />
            <Field
              label="Script"
              htmlFor="value"
              required
              help="Sent base64-encoded. Use Preview to dry-run before saving."
            >
              <div className="mb-1.5 flex justify-end">
                <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scriptHighlight}
                    onChange={(e) => setScriptHighlight(e.target.checked)}
                    className="rounded border-border"
                    disabled={updateMutation.isPending || isNft}
                  />
                  Syntax highlighting
                </label>
              </div>
              <CodeField
                highlight={scriptHighlight}
                language="shell"
                value={value}
                onChange={setValue}
                editable={!(updateMutation.isPending || isNft)}
                rows={10}
                placeholder={`#!/bin/bash\n# Print a single integer to stdout\necho 42`}
                ariaLabel="Measurement script"
                data-testid="value-editor"
              />
            </Field>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePreview}
                disabled={
                  previewMutation.isPending ||
                  !value.trim() ||
                  !measurementContainer.trim() ||
                  isNft
                }
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {previewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Preview run
              </button>
            </div>
            {preview && <PreviewResult resp={preview} />}
          </div>
        );
    }
  }
}

// Initial value derivation: each per-type case maps the API field to a
// human-editable string, including base64-decode for the types whose
// `value` is encoded on the wire (String / Collection / Measurement).
function initialValue(resource: Resource, type: ResourceType): string {
  const raw = resource.value ?? '';
  switch (type) {
    case 'String':
    case 'Collection':
    case 'Measurement':
      return fromBase64(raw);
    case 'Double':
      return prettyDouble(raw);
    default:
      return raw;
  }
}

// Soft limit is plain on the wire for every type that supports it.
// String / StringList / Collection don't support soft limits — those
// types ignore the field, so returning '' is correct (and matches the
// server's omitempty behaviour on the round-trip GET).
function initialSoftLimit(resource: Resource, _type: ResourceType): string {
  return resource.softLimit ?? '';
}
